import React, { useContext, useRef, cloneElement } from 'react';
import classnames from 'classnames';
import { getContext, useCombinedRefs } from 'common';
import { ListContext } from 'components/List';
import { CONSTANTS } from 'common/ListKeyboardNav';
import Badge from 'components/Badge';

import { SideDrawerContext, SideDrawerListContext } from './';
import Styled, { COMPACT_NAVIGATION_DRAWER_WIDTH, COMPACT_ICON_SIZE, SideDrawerVerticalBar } from './styles';

import DeprecatedRippleEffect from '../RippleEffect/DeprecatedRippleEffect';

const {
  LIST_ITEM_SELECTED,
  LIST_ITEM_TABABLE_ITEM
} = CONSTANTS;

const DEFAULT_WRAPPER_PROPS = {
  tabIndex: -1,
  onFocus: event => event.preventDefault()
};

const COMPACT_BADGE_STYLE = RTL => ({
  position: 'absolute' as const,
  margin: 0,
  top: '50%',
  left: !RTL ? '50%' : undefined,
  right: RTL ? '50%' : undefined,
  transform: 'translate(-50%, -50%)',
});

export interface SideDrawerItemProps extends React.ComponentPropsWithRef<'div'> {
  /** if `true`, displays the list item as selected. */
  selected?: boolean;
  /** if `true`, displays the list item as active which applies to the children of an expanded item. */
  active?: boolean;
  /** if `true`, displays the list item as a group item. */
  groupItem?: boolean;
  /** Set `true`, to render the label in group header style. */
  groupHeader?: boolean;
  /** Icon to use as an expandable item indicator. */
  expandableHeaderIndicator?: React.ReactNode;
  /** Indicates the expandable header is opened and is styled accordingly. */
  isOpen?: boolean;
  /** Set `true`, if heading item has an icon. Used for spacing purposes. */
  groupHeaderHasIcon?: boolean;
  /** The label of the item. */
  label?: React.ReactNode;
  /** The icon of the item. */
  icon?: React.ReactNode;
  /** The badge of the item. */
  badge?: React.ReactNode;
  /** To make custom content */
  children?: React.ReactNode;
  /** If `true`, the button will be rendered without ripple effect. */
  disabledRipple?: boolean;
  /** Ripple style. */
  rippleStyle?: React.CSSProperties;
  /** @ignore */
  style?: React.CSSProperties;
}

/** `SideDrawerItem` is made to be used as child of `SideDrawerList`. All other props than `SideDrawerItem`'s props will be provided to the list item DOM element */
const SideDrawerItem = React.forwardRef<HTMLElement, SideDrawerItemProps>((props: SideDrawerItemProps, forwardedRef) => getContext(({ RTL, theme }) => {
  const {
    children,
    selected,
    active,
    style,
    icon,
    label,
    badge,
    groupHeader,
    expandableHeaderIndicator,
    isOpen,
    groupHeaderHasIcon,
    groupItem,
    disabledRipple,
    rippleStyle,
    ...otherProps
  } = props;
  const listContext = useContext(ListContext);
  const sidedrawerContext = useContext(SideDrawerContext);
  const sidedrawerListContext = useContext(SideDrawerListContext);
  const innerRef = useRef(null);
  const ref = useCombinedRefs(forwardedRef, innerRef);
  const isCompact = sidedrawerContext.variant === 'compact';
  const isVisible = sidedrawerContext.visible;
  const isCompactBadge = isCompact && !isVisible;
  const classes = classnames({
    [`${LIST_ITEM_TABABLE_ITEM}-${listContext.uniqueListId}`]: (groupHeader && expandableHeaderIndicator) || !groupHeader,
    [LIST_ITEM_SELECTED]: selected,
  });

  const RIPPLE_STYLE = {
    width: '100%',
    pointerEvents: groupHeader && !expandableHeaderIndicator ? 'none' as const : undefined,
    maxWidth: isCompact && !isVisible ? COMPACT_NAVIGATION_DRAWER_WIDTH : undefined,
  };

  const resolveIcon = !!icon && cloneElement(icon as React.ReactElement, {
    color: (icon as React.ReactElement).props.color || selected ? theme.LIST.VERTICAL.sidedrawer.selected : theme.LIST.VERTICAL.icon,
    size: isCompact ? COMPACT_ICON_SIZE : undefined,
  });
  // Badge border color must match background color
  const resolvedBadgeBorderColor = selected ? theme.LIST.VERTICAL.selected.background : sidedrawerContext.background;
  const hasActiveBar = groupItem; // add to expanded children
  return (
    <DeprecatedRippleEffect
      style={{ ...RIPPLE_STYLE, ...rippleStyle }}
      as="li"
      disabled={disabledRipple !== undefined ? disabledRipple : sidedrawerContext.disableAnimation || selected}
    >
      <Styled.SideDrawerItem
        {...DEFAULT_WRAPPER_PROPS}
        className={classes}
        ref={ref}
        RTL={RTL}
        $theme={theme.LIST.VERTICAL}
        selected={selected}
        active={hasActiveBar}
        hoverEffect={listContext.hoverEffect}
        groupItem={groupItem}
        groupHeaderHasIcon={groupHeaderHasIcon}
        $isCompactBadge={isCompactBadge}
        border={!isVisible}
        $hoverBorderColor={selected ? theme.LIST.VERTICAL.selected.hovered.background : theme.LIST.VERTICAL.sidedrawer.badge.hover.border}
        role={
          groupHeader && !expandableHeaderIndicator ? 'presention' : undefined
        }
        {...otherProps}
      >
        <SideDrawerVerticalBar RTL={RTL} selected={selected} active={hasActiveBar} $theme={theme.LIST.VERTICAL} />
        <Styled.SideDrawerItemContent
          RTL={RTL}
          groupHeaderHasIcon={groupHeaderHasIcon}
          $hasIcon={icon !== undefined}
          $groupItem={groupItem}
          $isCompact={isCompact}
          $isSlim={sidedrawerListContext.variant === 'slim'}
          $isAllSlim={sidedrawerListContext.variant === 'all-slim'}
        >
          {!!resolveIcon && (
            <Styled.IconWrapper RTL={RTL} $isCompact={isCompact}>
              {!!badge && isCompactBadge && (
                <Styled.CompactBadgeWrapper RTL={RTL}>
                  <Badge style={COMPACT_BADGE_STYLE(RTL)} border={!isVisible} borderColor={resolvedBadgeBorderColor} />
                </Styled.CompactBadgeWrapper>
              )}
              {resolveIcon}
            </Styled.IconWrapper>
          )}
          {!!label && (
            <Styled.LabelWrapper
              selected={selected}
              groupHeader={groupHeader}
              $theme={theme.LIST.VERTICAL}
              $isOpen={isOpen}
            >
              {label}
            </Styled.LabelWrapper>
          )}
          {!!badge && !isCompactBadge && (
            <Styled.BadgeWrapper RTL={RTL}>{badge}</Styled.BadgeWrapper>
          )}
          {expandableHeaderIndicator && (
            <Styled.ExpandableIndicatorWrapper $hasBadge={!!badge}>
              {expandableHeaderIndicator}
            </Styled.ExpandableIndicatorWrapper>
          )}
          {children}
        </Styled.SideDrawerItemContent>
      </Styled.SideDrawerItem>
    </DeprecatedRippleEffect>
  );
}));

SideDrawerItem.displayName = 'SideDrawerItem';

SideDrawerItem.defaultProps = {
  selected: false,
  active: false,
  groupItem: false,
  groupHeader: false,
  expandableHeaderIndicator: undefined,
  isOpen: false,
  groupHeaderHasIcon: false,
  label: undefined,
  icon: undefined,
  badge: undefined,
  children: undefined,
  disabledRipple: undefined,
  rippleStyle: {},
  style: {}
};

export default SideDrawerItem;